import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SUBJECT_NAMES } from '@padcanvas/shared';
import { SimulationPlayer } from '../simulation-player/SimulationPlayer';
import type { SimulationListing, SimulationsSource } from './types';
import './simulations.css';

interface Props<T extends SimulationListing> {
  source: SimulationsSource<T>;
  /** Where the gallery lives, for the way back. */
  basePath?: string;
  /** The curator's view: says so when a simulation has no chapters yet, which
   *  is a job to do in admin and nothing a teacher can act on. */
  curation?: boolean;
  /**
   * Anything the surface adds beside the heading — admin's "Edit metadata".
   * Handed the simulation and a way to replace it, so an edit shows at once.
   */
  renderActions?: (sim: T, replace: (next: T) => void) => ReactNode;
}

/**
 * One simulation, the way the public site shows it.
 *
 * Deliberately the SAME page as sciencexp-app's simulations/[id].astro: same
 * order, same shared player, same sections. Admin checking a simulation is
 * asking "what will a student see?", and a teacher is about to put it on a
 * projector — a preview that differs from the real thing answers neither. The
 * v2 player is the shared component every surface uses, so the controls,
 * readouts and fullscreen behave identically rather than being approximated by
 * a bare iframe.
 */
export function SimulationDetail<T extends SimulationListing>({
  source,
  basePath = '/simulations',
  curation = false,
  renderActions,
}: Props<T>) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [sim, setSim] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<{ id: string; displayName: string }[]>([]);

  useEffect(() => {
    if (!id) return;
    source
      .get(id)
      .then((s) => setSim(s ?? null))
      .catch(() => setSim(null))
      .finally(() => setLoading(false));
  }, [id, source]);

  // Chapter names for the sidebar. The public page has them baked into its
  // static catalogue; here they have to be resolved. Best-effort: a failure
  // costs the names, not the page.
  useEffect(() => {
    if (!sim) return;
    source
      .moduleNames(sim.subject)
      .then(setModules)
      .catch(() => setModules([]));
  }, [sim?.subject, source]);

  if (loading) {
    return <div className="sim-page"><div className="sim-empty">Loading…</div></div>;
  }

  if (!sim) {
    return (
      <div className="sim-page">
        <button className="sim-back" onClick={() => navigate(basePath)}>
          &larr; Back to simulations
        </button>
        <div className="sim-empty">Simulation not found.</div>
      </div>
    );
  }

  const byId = new Map(modules.map((m) => [m.id, m]));
  const chapterNames = (sim.moduleIds ?? []).map((mid) => byId.get(mid)?.displayName ?? mid);

  return (
    <div className="sim-page sim-detail">
      <button className="sim-back" onClick={() => navigate(basePath)}>
        &larr; All simulations
      </button>

      <div className="sim-detail-head">
        <div className="sim-detail-heading">
          <span className="sim-detail-eyebrow">
            {SUBJECT_NAMES[sim.subject] ?? sim.subject} · {sim.topic}
          </span>
          <h1>{sim.title}</h1>
          <p className="sim-detail-lede">{sim.tagline}</p>
        </div>
        {(curation && sim.status === 'draft') || renderActions ? (
          <div className="sim-detail-actions">
            {curation && sim.status === 'draft' && <span className="sim-card-draft">Draft</span>}
            {renderActions?.(sim, setSim)}
          </div>
        ) : null}
      </div>

      {/* A v2 asset declares a manifest and gets the shared player — the same
          controls, readouts and fullscreen the science site and the student app
          show. Anything else is a legacy page and keeps the plain embed. */}
      {sim.runtime === 2 ? (
        <div className="sim-frame-wrap sim-frame-v2">
          <SimulationPlayer src={sim.assetUrl} title={sim.title} />
        </div>
      ) : (
        <div className="sim-frame-wrap">
          <iframe className="sim-frame" src={sim.assetUrl} title={sim.title} loading="lazy" />
        </div>
      )}

      <div className="sim-detail-actions-row">
        {/* v2 owns its own fullscreen (the player's ⛶), so a second control here
            would target a different element and leave the player unaware. */}
        {sim.runtime !== 2 && (
          <a className="sim-open-new" href={sim.assetUrl} target="_blank" rel="noreferrer">
            Open full screen ↗
          </a>
        )}
        <a className="sim-open-new" href={sim.assetUrl} target="_blank" rel="noreferrer">
          Open in new tab ↗
        </a>
      </div>

      <div className="sim-detail-body">
        <section className="sim-detail-main">
          <h2>About this simulation</h2>
          <p>{sim.description}</p>
        </section>

        <aside className="sim-detail-side">
          <section>
            <h3>What you can change</h3>
            <ul className="sim-detail-params">
              {sim.parameters.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </section>
          {(chapterNames.length > 0 || curation) && (
            <section>
              <h3>Chapters</h3>
              {chapterNames.length === 0 ? (
                <p className="sim-detail-none">None yet — matches by subject + topic only.</p>
              ) : (
                <ul className="sim-detail-chapters">
                  {chapterNames.map((n) => <li key={n}>{n}</li>)}
                </ul>
              )}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
